<template>
  <CModal
    :show.sync="show"
    centered
    size="xl"
    class="photo-archive-modal"
  >
    <template #header>
      <div class="d-flex align-items-center">
        <!-- When filtering by person: <- Gallery Name > Person's photos -->
        <template v-if="canGoBack">
          <CLink class="d-flex align-items-center breadcrumb-item" @click="onGoBackToOrigin">
            <CIcon name="cipArrowNarrowLeft" size="custom-size" :width="16"/>
            <div class="v-separator"></div>
            {{ galleryName }}
          </CLink>
          <CIcon name="cipChevronRight" size="custom-size" :width="14" class="mx-2 text-muted"/>
          <span class="photo-archive-modal__title breadcrumb-item">{{ activeClusterPhotosLabel }}</span>
        </template>
        <!-- Default: <- Back to gallery | Gallery Name -->
        <template v-else>
          <CLink class="d-flex align-items-center breadcrumb-item" @click="close">
            <CIcon name="cipArrowNarrowLeft" size="custom-size" :width="16"/>
            <div class="v-separator"></div>
            <translate translate-context="photoarchive.header">Back to gallery</translate>
          </CLink>
          <span class="photo-archive-modal__title breadcrumb-item">{{ galleryName }}</span>
        </template>
      </div>
      <CButtonClose @click="close"/>
    </template>

    <transition name="slide-fade-left" mode="out-in">
      <div
        class="photo-archive-modal__content"
        v-if="currentPhoto"
        :key="`content-${contentSlideKey}`"
      >
      <PContextMenu ref="contextMenu" :menu-items="contextItems" @item-selected="onContextMenuSelected"/>

      <!-- Main Layout: Left List + Center Viewer + Right Panel -->
      <div class="photo-archive-modal__layout">
        <!-- Left: Photo List -->
        <div class="photo-archive-modal__list">
          <div class="photo-archive-modal__list-header">
            <!-- Person filter indicator (only shown when filtering by cluster) -->
            <div
              v-if="(activeContextKey==='clusterFiltered' || activeContextKey === 'clusterAll') && activeClusterId"
              class="photo-archive-modal__person-filter"
            >
              <img
                v-if="activeClusterThumbUrl"
                :src="activeClusterThumbUrl"
                class="photo-archive-modal__person-filter-avatar"
                alt=""
              />
              <CIcon v-else name="cipUser" size="custom-size" :width="16"/>
              <span class="photo-archive-modal__person-filter-name">{{ activeClusterDisplayName }}</span>
              <div class="cursor-pointer" @click="onGoBackToOrigin">
                <CIcon name="cipXCircle" size="custom-size" :width="16"
                       class="photo-archive-modal__person-filter-close"/>
              </div>
            </div>

            <!-- Tabs (only shown when cluster filter is active) -->
            <PTabs
              :tabs="listTabs"
              :selected="activeContextKey"
              style="margin:3px 0 2px"
              :fullWidth="false"
              @select="onSwitchContext($event)"
              v-if="showClusterTabs"
            />
          </div>
          <div class="photo-archive-modal__list-items" ref="photoListContainer">
            <div v-if="loadingBefore" class="photo-archive-modal__list-loader">
              <CIcon name="cipCircleLoader" size="custom-size" class="text-primary" :width="24" />
            </div>
            <div ref="loadBeforeSentinel" class="photo-archive-modal__list-sentinel"></div>
            <div
              v-for="photo in photoList"
              :key="photo.id"
              :data-photo-id="photo.id"
              class="photo-archive-modal__list-item"
              :class="{ 'active': photo.id === currentPhotoId }"
              @click="onPhotoSelect(photo.id)"
            >
              <img
                :src="photo.thumb"
                :alt="photo.file_name"
                loading="lazy"
              />
            </div>
            <div ref="loadAfterSentinel" class="photo-archive-modal__list-sentinel"></div>
            <div v-if="loadingAfter" class="photo-archive-modal__list-loader">
              <CIcon name="cipCircleLoader" size="custom-size" :width="24" class="text-primary"/>
            </div>
          </div>
          <div class="photo-archive-modal__list-footer">
            <CButton
              color="secondary"
              block
              :disabled="downloadingAll"
              @click="onDownloadAll"
            >
              <CIcon
                :name="downloadingAll ? 'cipCircleLoader' : 'cipDownload03'"
                :class="{ 'rotating': downloadingAll }"
              />
              <template v-if="!downloadingAll">
                <translate translate-context="photoarchive.action">Download all</translate>
                <span class="ml-1">({{ totalPhotos }})</span>
              </template>
            </CButton>
          </div>
        </div>

        <!-- Center: Image Viewer with Tabs -->
        <div class="photo-archive-modal__viewer-section">
          <!-- Tabs and navigation above the image -->
          <div class="photo-archive-modal__header">
            <PTabs
              :tabs="panelTabs"
              :selected="panelTab"
              :full-width="false"
              style="margin-bottom:6px;"
              @select="panelTab = $event"
            />

            <div class="photo-archive-modal__nav">
              <CLink @click="navigatePrevPhoto" :disabled="!hasPrev" class="photo-archive-modal__nav-btn">
                <CIcon name="cipChevronLeft" size="custom-size" :width="18"/>
              </CLink>
              <span class="photo-archive-modal__nav-text">
                {{ displayPosition }} <translate
                translate-context="photoarchive.nav">su</translate> {{ totalPhotos }}
              </span>
              <CLink @click="navigateNextPhoto" :disabled="!hasNext" class="photo-archive-modal__nav-btn">
                <CIcon name="cipChevronRight" size="custom-size" :width="18"/>
              </CLink>
            </div>
          </div>

          <!-- Image Viewer -->
          <div class="photo-archive-modal__viewer">
            <ImageViewer
              :photo="currentPhoto"
              :selected-face-id="panelTab === 'people' ? selectedFaceId : null"
              :highlighted-face-id="panelTab === 'people' ? highlightedFaceId : null"
              :show-face-boxes="panelTab === 'people'"
              :cluster-highlight-pattern="clusterHighlightPattern"
              :navigation-direction="navigationDirection"
              @face-highlight="onFaceHighlight"
              @face-clear-highlight="onFaceClearHighlight"
              @face-select="onFaceSelect"
              @face-context-menu="onMenuOpen"
            />
          </div>
        </div>

        <!-- Right Panel: Changes based on active tab -->
        <div class="photo-archive-modal__panel">
          <!-- Photo Tab Panel -->
          <template v-if="panelTab === 'photo'">
            <div class="photo-archive-modal__panel-header">
              <div class="photo-archive-modal__panel-title">
                <CIcon name="cipImage03" size="custom-size" :width="18" class="mr-2" style="color: #8887A0"/>
                <translate translate-context="photoarchive.panel">Photo</translate>
              </div>
              <CLink class="photo-archive-modal__download" @click="onDownload">
                <translate translate-context="photoarchive.action">Download</translate>
                <CIcon name="cipDownload03" size="custom-size" :width="18" class="ml-1"/>
              </CLink>
            </div>
            <transition name="fade" mode="out-in" appear>
              <div class="photo-archive-modal__panel-content" :key="currentPhotoId">
                <!-- File Info -->
                <div class="photo-archive-modal__info-section">
                  <div class="photo-archive-modal__info-row">
                  <span class="photo-archive-modal__info-label">
                    <translate translate-context="photoarchive.info">File name</translate>
                  </span>
                    <span class="photo-archive-modal__info-value">{{
                        currentPhoto.original_filename || currentPhoto.fileName
                      }}</span>
                  </div>
                  <div class="photo-archive-modal__info-row">
                  <span class="photo-archive-modal__info-label">
                    <translate translate-context="photoarchive.info">Resolution</translate>
                  </span>
                    <span class="photo-archive-modal__info-value">{{ photoResolution }}</span>
                  </div>
                  <div class="photo-archive-modal__info-row" v-if="currentPhoto.shot_at">
                  <span class="photo-archive-modal__info-label">
                    <translate translate-context="photoarchive.info">Shot at</translate>
                  </span>
                    <span class="photo-archive-modal__info-value">{{ formatDateTime(currentPhoto.shot_at) }}</span>
                  </div>
                  <div class="photo-archive-modal__info-row">
                  <span class="photo-archive-modal__info-label">
                    <translate translate-context="photoarchive.info">Uploaded</translate>
                  </span>
                    <span class="photo-archive-modal__info-value">{{ formatDateTime(currentPhoto.created_at) }}</span>
                  </div>
                  <div class="photo-archive-modal__info-row" v-if="currentPhoto.photographer">
                  <span class="photo-archive-modal__info-label">
                    <translate translate-context="photoarchive.info">Photographer</translate>
                  </span>
                    <span class="photo-archive-modal__info-value">{{
                        currentPhoto.photographer.email || currentPhoto.photographer
                      }}</span>
                  </div>
                  <div class="photo-archive-modal__info-row"
                       v-if="currentPhotoMetadata && currentPhotoMetadata.file_size">
                  <span class="photo-archive-modal__info-label">
                    <translate translate-context="photoarchive.info">File size</translate>
                  </span>
                    <span class="photo-archive-modal__info-value">{{
                        formatFileSize(currentPhotoMetadata.file_size)
                      }}</span>
                  </div>
                </div>

                <!-- Technical Metadata Box -->
                <div class="photo-archive-modal__metadata-box" v-if="currentPhotoMetadata && hasAnyMetadata">
                  <div class="photo-archive-modal__metadata-box-title">
                    <CIcon name="cipCameraLens" size="custom-size" :width="12" class="mr-1"/>
                    <translate translate-context="photoarchive.info">EXIF</translate>
                  </div>
                  <div class="photo-archive-modal__metadata-box-content">
                  <span v-if="currentPhotoMetadata.camera_make || currentPhotoMetadata.camera_model"
                        class="photo-archive-modal__metadata-item">
                    {{
                      [currentPhotoMetadata.camera_make, currentPhotoMetadata.camera_model].filter(Boolean).join(' ')
                    }}
                  </span>
                    <span v-if="currentPhotoMetadata.lens_model" class="photo-archive-modal__metadata-item">
                    {{ currentPhotoMetadata.lens_model }}
                  </span>
                    <span v-if="currentPhotoMetadata.focal_length" class="photo-archive-modal__metadata-item">
                    {{ currentPhotoMetadata.focal_length }}mm
                  </span>
                    <span v-if="currentPhotoMetadata.aperture" class="photo-archive-modal__metadata-item">
                    f/{{ currentPhotoMetadata.aperture }}
                  </span>
                    <span v-if="currentPhotoMetadata.shutter_speed" class="photo-archive-modal__metadata-item">
                    {{ currentPhotoMetadata.shutter_speed }}s
                  </span>
                    <span v-if="currentPhotoMetadata.iso" class="photo-archive-modal__metadata-item">
                    ISO {{ currentPhotoMetadata.iso }}
                  </span>
                  </div>
                </div>


                <!-- Tags Section -->
                <div class="photo-archive-modal__tags-section" v-if="mediaTagDefinitions.length > 0">
                  <TagsSectionCard
                    ref="mediaTagsCard"
                    :key="`media-${currentPhotoId}`"
                    :title="$pgettext('photoarchive.data', 'Tag')"
                    :definitions="mediaTagDefinitions"
                    :tags="currentMediaTags"
                    @save="onUpdateMediaTags"
                  />
                </div>
              </div>
            </transition>
          </template>

          <!-- People Tab Panel -->
          <template v-else-if="panelTab === 'people'">
            <div class="photo-archive-modal__panel-header">
              <div class="photo-archive-modal__panel-title">
                <CIcon name="cipUser" size="custom-size" :width="18" class="mr-2" style="color: #8887A0"/>
                <translate translate-context="photoarchive.panel">People</translate>
              </div>
              <CLink
                v-if="showViewClusterPhotosButton"
                class="photo-archive-modal__cluster-link"
                @click="onViewClusterPhotos"
              >
                <translate
                  translate-context="photoarchive.action"
                  :translate-params="{ name: selectedPersonName, count: clusterPhotosCount }"
                >%{name}'s photos (%{count})
                </translate>
                <CIcon name="cipSearch" size="custom-size" :width="14" class="ml-1"/>
              </CLink>
            </div>

            <div class="photo-archive-modal__panel-content">
              <!-- Faces Slider -->
              <div class="photo-archive-modal__faces-slider" v-if="sortedFaces.length > 0">
                <button
                  class="photo-archive-modal__faces-arrow photo-archive-modal__faces-arrow--left"
                  @click="scrollFacesLeft"
                  :disabled="!canScrollFacesLeft"
                >
                  <CIcon name="cipChevronLeft" size="custom-size" :width="16"/>
                </button>

                <div class="photo-archive-modal__faces-track" ref="facesTrack" @scroll="updateFacesScrollState">
                  <div
                    v-for="face in sortedFaces"
                    :key="face.id"
                    :ref="'face-' + face.id"
                    class="photo-archive-modal__face-item"
                    :class="{ 'selected': selectedFaceId === face.id, 'incomplete': face.tagsCompletionStatus === TagsCompletionStatus.EMPTY }"
                    @click="onFaceSelect(face.id)"
                    @mouseover="onFaceHighlight(face.id)"
                    @mouseleave="onFaceClearHighlight"
                  >
                    <div class="photo-archive-modal__face-avatar">
                      <img :src="face.thumb_url" alt=""/>
                    </div>
                    <span :class="['photo-archive-modal__face-name', getFaceName(face) ? '' : 'unknow']">
                      {{ getFaceName(face) || '------'}}
                    </span>
                  </div>
                </div>

                <button
                  class="photo-archive-modal__faces-arrow photo-archive-modal__faces-arrow--right"
                  @click="scrollFacesRight"
                  :disabled="!canScrollFacesRight"
                >
                  <CIcon name="cipChevronRight" size="custom-size" :width="16"/>
                </button>
              </div>

              <div v-else class="photo-archive-modal__no-faces">
                <translate translate-context="photoarchive.faces">No faces detected</translate>
              </div>

              <!-- Cluster Form (when face is selected) -->
              <div class="photo-archive-modal__cluster-form" v-if="selectedFaceId && selectedCluster">
                <!-- Cluster Tags -->
                <TagsSectionCard
                  ref="clusterTagsCard"
                  v-if="clusterTagDefinitions.length > 0"
                  :key="`cluster-${currentPhotoId}-${selectedCluster.id}`"
                  :title="$pgettext('photoarchive.data', 'Persona')"
                  :definitions="clusterTagDefinitions"
                  :tags="currentClusterTags"
                  @save="onSaveClusterTags"
                />
              </div>

              <div v-else-if="sortedFaces && sortedFaces.length > 0" class="photo-archive-modal__select-face">
                <translate translate-context="photoarchive.data">
                  Select a face to view and edit details
                </translate>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else class="photo-archive-modal__loading">
      <CIcon name="cipCircleLoader" size="custom-size" :width="48" class="text-primary"/>
    </div>
    </transition>

    <template #footer-wrapper><span></span></template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex"
import ImageViewer from "@/domain/photoArchive/components/ImageViewer.vue"
import TagsSectionCard from "@/domain/photoArchive/components/TagsSectionCard.vue"
import PContextMenu from "@/domain/core/components/PContextMenu.vue"
import PTabs from "@/domain/core/components/PTabs.vue"
import TagsCompletionStatus from "@/domain/core/constant/tagsCompletionStatus"
import { applyPattern } from '@/domain/core/utils/pattern'
import downloadStrategy from "@/domain/downloader/downloadStrategy"

export default {
  name: "PhotoArchiveModal",
  components: {
    PTabs,
    ImageViewer,
    TagsSectionCard,
    PContextMenu
  },
  data() {
    return {
      show: false,
      panelTab: 'photo', // Right panel tab: 'photo' or 'people'
      galleryName: '',
      canScrollFacesLeft: false,
      canScrollFacesRight: false,
      navigationDirection: 0, // -1 = prev (up), 1 = next (down), 0 = no direction
      TagsCompletionStatus, // Expose constant for template
      downloadingAll: false,
      loadingBefore: false,
      loadingAfter: false,
      beforeObserver: null,
      afterObserver: null,
      // For scroll position restore when prepending
      scrollHeightBefore: 0,
      scrollTopBefore: 0,
      // Block fetches until initial scroll is done
      initialScrollDone: false,
      contentTransitionNonce: 0
    }
  },
  computed: {
    ...mapGetters('photoArchive', [
      'currentPhotoId',
      'currentPhoto',
      'currentPhotoMetadata',
      'loadingMetadata',
      'photoList',
      'totalPhotos',
      'currentPosition',
      'selectedFaceId',
      'selectedFace',
      'selectedFaceClusterCount',
      'selectedCluster',
      'highlightedFaceId',
      'loading',
      'hasPrev',
      'hasNext',
      'hasBefore',
      'hasAfter',
      'tagDefinitionsByScope',
      'clusterHighlightPattern',
      'clusterNamePattern',
      // Context system getters
      'activeContextKey',
      'showClusterTabs',
      'canGoBack',
      'activeCluster',
      'activeClusterId',
      'activeClusterName',
      'activeClusterThumbUrl',
      'browseCount',
      'clusterFilteredCount',
      'clusterAllCount'
    ]),
    ...mapGetters('photoSearch', [
      'activeFilters'
    ]),
    panelTabs() {
      return [
        {
          name: 'photo',
          label: this.$pgettext('photoarchive.tabs', 'Photo'),
          icon: 'cipImage03'
        },
        {
          name: 'people',
          label: this.$pgettext('photoarchive.tabs', 'People'),
          icon: 'cipUser'
        }
      ]
    },
    listTabs() {
      return [
        {
          name: 'clusterFiltered',
          label: `${this.$pgettext('photoarchive.tabs', 'Filtered')} (${this.clusterFilteredCount})`
        },
        {
          name: 'clusterAll',
          label: `${this.$pgettext('photoarchive.tabs', 'All photos')} (${this.clusterAllCount})`
        }
      ]
    },
    hasActiveFilters() {
      return this.activeFilters && this.activeFilters.length > 0
    },
    activeFiltersTooltip() {
      if (!this.activeFilters || this.activeFilters.length === 0) return ''

      const filterLabels = this.activeFilters.map(filter => {
        if (filter.type === 'date_range') {
          return this.$pgettext('photoarchive.filter', 'Date range')
        }
        if (filter.type === 'photographer') {
          return this.$pgettext('photoarchive.filter', 'Photographer')
        }
        if (filter.type === 'tags') {
          return this.$pgettext('photoarchive.filter', 'Tags')
        }
        if (filter.type === 'pica_code') {
          return `PicaCode: ${filter.value}`
        }
        if (filter.type === 'cluster_id') {
          return this.$pgettext('photoarchive.filter', 'Person')
        }
        if (filter.type === 'deleted') {
          return this.$pgettext('photoarchive.filter', 'Hidden photos')
        }
        if (filter.type === 'unprocessed') {
          return this.$pgettext('photoarchive.filter', 'Unprocessable')
        }
        return filter.type
      })

      return filterLabels.join(', ')
    },
    mediaTagDefinitions() {
      return this.tagDefinitionsByScope('media')
    },
    faceTagDefinitions() {
      return this.tagDefinitionsByScope('face')
    },
    clusterTagDefinitions() {
      return this.tagDefinitionsByScope('cluster')
    },
    currentMediaTags() {
      return this.currentPhoto?.tags?.media || {}
    },
    currentFaceTags() {
      if (!this.selectedFace?.tags) return {}
      return this.selectedFace.tags.face || this.selectedFace.tags || {}
    },
    currentClusterTags() {
      // Cluster tags are now flat in selectedCluster.tags (API change)
      return this.selectedCluster?.tags || {}
    },
    sortedFaces() {
      if (!this.currentPhoto?.faces) return []
      return [...this.currentPhoto.faces].sort((a, b) => a.face_box.x - b.face_box.x)
    },
    // Position and total now come from store (based on active context)
    displayPosition() {
      return this.currentPosition || 1
    },
    photoResolution() {
      if (!this.currentPhoto?.width || !this.currentPhoto?.height) return '-'
      return `${this.currentPhoto.width} x ${this.currentPhoto.height}`
    },
    selectedPersonName() {
      const fallback = this.$pgettext('photoarchive.fallback', 'Person')
      if (!this.selectedCluster?.tags) return fallback
      // Cluster tags are now flat in selectedCluster.tags (API change)
      return applyPattern(this.selectedCluster.tags, this.clusterNamePattern, fallback)
    },
    activeClusterDisplayName() {
      // Use stored name (computed when filter was set)
      return this.activeClusterName || this.$pgettext('photoarchive.fallback', 'Selected')
    },
    panelSlideKey() {
      // Changes when entering/exiting cluster view to trigger transition
      return this.canGoBack ? `cluster-${this.currentPhotoId}` : `default-${this.currentPhotoId}`
    },
    // Outer content transition key: only depends on view mode, not on photo id
    contentSlideKey() {
      const mode = this.canGoBack ? 'cluster' : 'default'
      return `${mode}-${this.contentTransitionNonce}`
    },
    activeClusterPhotosLabel() {
      // Uses the pattern like "{name}'s photos" with actual person name
      const name = this.activeClusterDisplayName
      return this.$pgettext('photoarchive.header', "%{name}'s photos").replace('%{name}', name)
    },
    clusterPhotosCount() {
      // Total photos of selected face's cluster (preview, not the active cluster)
      return this.selectedFaceClusterCount || 0
    },
    showViewClusterPhotosButton() {
      // Don't show button if this cluster is already the active filter
      if (!this.selectedCluster) return false
      if (this.activeClusterId && this.selectedCluster.id === this.activeClusterId) return false
      return true
    },
    hasAnyMetadata() {
      if (!this.currentPhotoMetadata) return false
      const m = this.currentPhotoMetadata
      return m.camera_make || m.camera_model || m.lens_model ||
        m.focal_length || m.aperture || m.shutter_speed || m.iso
    },
    contextItems() {
      const actions = []

      if (this.selectedCluster) {
        // View all photos of this person
        actions.push({
          value: 'view_cluster_photos',
          label: this.$pgettext('photoarchive.action', 'Vedi tutte le foto'),
          icon: 'cipSearch'
        })

        if (this.hasPerm('photo_search.archive.link_cluster_picacode') && !this.selectedCluster.pica_code) {
          actions.push({
            value: 'link_cluster_pica_code',
            label: this.$pgettext('photoarchive.action', 'Collega Pica Code'),
            icon: 'cipLink01'
          })
        }

        if (this.hasPerm('photo_search.archive.unlink_cluster_picacode') && this.selectedCluster.pica_code) {
          actions.push({
            value: 'unlink_cluster_pica_code',
            label: this.$pgettext('photoarchive.action', 'Scollega Pica Code'),
            icon: 'cipLinkBreak02'
          })
        }

        if (this.hasPerm('photo_search.archive.delete_cluster')) {
          actions.push({divider: true})
          actions.push({
            value: 'delete_cluster',
            label: this.$pgettext('photoarchive.action', 'Elimina cluster'),
            icon: 'cipTrashFull'
          })
        }
      }

      if (this.hasPerm('photo_search.archive.delete_face')) {
        if (actions.length) {
          actions.push({divider: true})
        }
        actions.push({
          value: 'delete_face',
          label: this.$pgettext('photoarchive.action', 'Elimina volto'),
          icon: 'cipTrashFull'
        })
      }

      return actions
    }
  },
  watch: {
    selectedFaceId(newId) {
      if (newId) {
        this.$nextTick(() => {
          this.scrollToSelectedFace()
        })
      }
    },
    currentPhotoId() {
      if (!this.initialScrollDone) return
      this.$nextTick(() => {
        this.scrollToActivePhoto()
        this.updateFacesScrollState()
      })
    },
    sortedFaces() {
      this.$nextTick(() => {
        this.updateFacesScrollState()
      })
    },
    // Handle scroll position when loading before (prepending items)
    async photoList() {
      if (this.loadingBefore) {
        const container = this.$refs.photoListContainer
        if (container && this.scrollHeightBefore > 0) {
          await this.$nextTick()
          const diff = container.scrollHeight - this.scrollHeightBefore
          container.scrollTop = this.scrollTopBefore + diff
        }
      }
    }
  },
  methods: {
    ...mapActions('photoArchive', [
      'init',
      'reset',
      'changePhoto',
      'selectFace',
      'highlightFace',
      'clearHighlight',
      'updateMediaTags',
      'updateFaceTags',
      'updateClusterTags',
      'deleteFace',
      'deleteCluster',
      'linkClusterPicaCode',
      'unlinkClusterPicaCode',
      'fetchCurrentPhoto',
      'fetchPhotoMetadata',
      'navigatePrev',
      'navigateNext',
      'viewClusterPhotos',
      'switchContext',
      'goBackToOrigin',
      'downloadContextPhotos',
      'fetchAfterMedia',
      'fetchBeforeMedia'
    ]),
    ...mapActions('photoSearch', [
      'downloadPhotos'
    ]),
    async open({picaServiceId, galleryId, photoId, galleryName}) {
      this.show = true
      this.panelTab = 'photo'
      this.galleryName = galleryName || ''

      this.initialScrollDone = false

      await this.init({
        picaServiceId,
        galleryId: galleryId || 0,
        photoId
      })

      // Delay scroll/setup to break out of the render cycle
      // Retry if container not ready yet
      const initScrollAndObservers = (retries = 5) => {
        if (!this.$refs.photoListContainer && retries > 0) {
          setTimeout(() => initScrollAndObservers(retries - 1), 50)
          return
        }
        this.updateFacesScrollState()
        this.scrollToActivePhoto(true)
        this.initialScrollDone = true
        this.setupInfiniteScroll()
      }
      setTimeout(() => initScrollAndObservers(), 50)

      document.addEventListener('keydown', this.handleKeydown)
    },
    async close() {
      // Check for pending changes before closing
      if (this.hasPendingChanges()) {
        const discard = await this.confirmDiscardChanges()
        if (!discard) return
        this.resetPendingChanges()
      }

      this.show = false
      this.destroyInfiniteScroll()
      document.removeEventListener('keydown', this.handleKeydown)
      // Reset store state when closing the modal
      this.reset()
    },
    hasPendingChanges() {
      const mediaCard = this.$refs.mediaTagsCard
      const clusterCard = this.$refs.clusterTagsCard
      return (mediaCard && mediaCard.hasPendingChanges()) ||
             (clusterCard && clusterCard.hasPendingChanges())
    },
    async confirmDiscardChanges() {
      return new Promise((resolve) => {
        this.confirm({
          color: 'warning',
          title: this.$pgettext('photoarchive.title', 'Unsaved changes'),
          icon: 'cipAlertTriangle',
          message: this.$pgettext('photoarchive.confirm', 'You have unsaved changes. Do you want to discard them?'),
          cb_confirm: () => resolve(true),
          cb_cancel: () => resolve(false)
        })
      })
    },
    resetPendingChanges() {
      const mediaCard = this.$refs.mediaTagsCard
      const clusterCard = this.$refs.clusterTagsCard
      if (mediaCard) mediaCard.resetDirty()
      if (clusterCard) clusterCard.resetDirty()
    },
    async handleKeydown(event) {
      // Prevent key repeat from firing multiple times
      if (event.repeat) return

      if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && this.hasPrev) {
        this.navigatePrevPhoto()
      } else if ((event.key === 'ArrowRight' || event.key === 'ArrowDown') && this.hasNext) {
        this.navigateNextPhoto()
      } else if (event.key === 'Escape') {
        await this.close()
      }
    },
    async onPhotoSelect(photoId) {
      if (photoId === this.currentPhotoId) return

      // Check for pending changes
      if (this.hasPendingChanges()) {
        const discard = await this.confirmDiscardChanges()
        if (!discard) return
        this.resetPendingChanges()
      }

      // Calculate direction based on position in list
      const currentIndex = this.photoList.findIndex(p => p.id === this.currentPhotoId)
      const newIndex = this.photoList.findIndex(p => p.id === photoId)
      if (newIndex < currentIndex) {
        this.navigationDirection = -1 // Going up
      } else if (newIndex > currentIndex) {
        this.navigationDirection = 1 // Going down
      } else {
        this.navigationDirection = 0 // Same photo
      }
      await this.changePhoto(photoId)
    },
    async navigatePrevPhoto() {
      // Check for pending changes
      if (this.hasPendingChanges()) {
        const discard = await this.confirmDiscardChanges()
        if (!discard) return
        this.resetPendingChanges()
      }

      this.navigationDirection = -1
      await this.navigatePrev()
    },
    async navigateNextPhoto() {
      // Check for pending changes
      if (this.hasPendingChanges()) {
        const discard = await this.confirmDiscardChanges()
        if (!discard) return
        this.resetPendingChanges()
      }

      this.navigationDirection = 1
      await this.navigateNext()
    },
    scrollToActivePhoto(instant = false) {
      const container = this.$refs.photoListContainer
      if (!container) return false

      const id = this.currentPhotoId + ''
      const elements = container.querySelectorAll('.photo-archive-modal__list-item')

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i]
        if (el.dataset.photoId === id) {
          el.scrollIntoView({ behavior: instant ? 'instant' : 'smooth', block: 'center' })
          return true
        }
      }
      return false
    },
    /**
     * Scroll to active photo with retry if container not ready
     */
    scrollToActivePhotoWithRetry(retries = 5) {
      setTimeout(() => {
        if (!this.scrollToActivePhoto(true) && retries > 0) {
          this.scrollToActivePhotoWithRetry(retries - 1)
        }
      }, 50)
    },
    setupInfiniteScroll() {
      this.destroyInfiniteScroll()

      const beforeSentinel = this.$refs.loadBeforeSentinel
      const afterSentinel = this.$refs.loadAfterSentinel
      const container = this.$refs.photoListContainer
      if (!container) return

      const observerOptions = {
        root: container,
        threshold: 0.1,
        rootMargin: '200px'
      }

      // Observer for loading before (scroll up)
      if (beforeSentinel) {
        this.beforeObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.loadBeforePhotos()
              }
            })
          },
          observerOptions
        )
        this.beforeObserver.observe(beforeSentinel)
      }

      // Observer for loading after (scroll down)
      if (afterSentinel) {
        this.afterObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.loadAfterPhotos()
              }
            })
          },
          observerOptions
        )
        this.afterObserver.observe(afterSentinel)
      }
    },
    destroyInfiniteScroll() {
      if (this.beforeObserver) {
        this.beforeObserver.disconnect()
        this.beforeObserver = null
      }
      if (this.afterObserver) {
        this.afterObserver.disconnect()
        this.afterObserver = null
      }
    },
    async loadBeforePhotos() {
      if (this.loadingBefore || !this.hasBefore) return

      const container = this.$refs.photoListContainer

      this.loadingBefore = true

      // Store scroll position before adding items (watcher will restore)
      if (container) {
        this.scrollHeightBefore = container.scrollHeight
        this.scrollTopBefore = container.scrollTop
      }

      try {
        await this.fetchBeforeMedia()
      } finally {
        this.loadingBefore = false
      }
    },
    async loadAfterPhotos() {
      if (this.loadingAfter || !this.hasAfter) return

      this.loadingAfter = true
      try {
        await this.fetchAfterMedia()
      } finally {
        this.loadingAfter = false
      }
    },
    onFaceHighlight(faceId) {
      this.highlightFace(faceId)
    },
    onFaceClearHighlight() {
      this.clearHighlight()
    },
    async onFaceSelect(faceId) {
      // Skip if selecting the same face
      if (faceId === this.selectedFaceId) return

      // Check for pending changes in cluster tags
      const clusterCard = this.$refs.clusterTagsCard
      if (clusterCard && clusterCard.hasPendingChanges()) {
        const discard = await this.confirmDiscardChanges()
        if (!discard) return
        clusterCard.resetDirty()
      }

      this.selectFace(faceId)
      if (this.panelTab !== 'people') {
        this.panelTab = 'people'
      }
    },
    getFaceName(face) {
      // Cluster tags are now in face.tags directly (API change)
      if (!face?.tags) return ''
      return applyPattern(face.tags, this.clusterNamePattern, '')
    },
    scrollToSelectedFace() {
      if (!this.selectedFaceId) return
      const faceRef = this.$refs['face-' + this.selectedFaceId]
      if (faceRef && faceRef[0]) {
        const track = this.$refs.facesTrack
        const face = faceRef[0]
        const trackRect = track.getBoundingClientRect()
        const faceRect = face.getBoundingClientRect()

        if (faceRect.left < trackRect.left || faceRect.right > trackRect.right) {
          face.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          })
        }
      }
      this.updateFacesScrollState()
    },
    scrollFacesLeft() {
      const track = this.$refs.facesTrack
      if (track) {
        track.scrollBy({left: -120, behavior: 'smooth'})
        setTimeout(() => this.updateFacesScrollState(), 300)
      }
    },
    scrollFacesRight() {
      const track = this.$refs.facesTrack
      if (track) {
        track.scrollBy({left: 120, behavior: 'smooth'})
        setTimeout(() => this.updateFacesScrollState(), 300)
      }
    },
    updateFacesScrollState() {
      const track = this.$refs.facesTrack
      if (track) {
        this.canScrollFacesLeft = track.scrollLeft > 0
        this.canScrollFacesRight = track.scrollLeft < (track.scrollWidth - track.clientWidth - 1)
      }
    },
    async onViewClusterPhotos() {
      if (!this.selectedCluster || !this.selectedFace) return

      // Switch to cluster view (shows filtered photos in left list)
      // If we are already in cluster view, bump the key to retrigger the transition
      if (this.canGoBack) {
        this.contentTransitionNonce++
        await this.$nextTick()
      }
      await this.viewClusterPhotos()
      this.scrollToActivePhotoWithRetry()
    },
    async onGoBackToOrigin() {
      // Go back to the origin photo and reset cluster filter
      await this.goBackToOrigin()
      this.scrollToActivePhotoWithRetry()
    },
    async onSwitchContext(contextKey) {
      // Switch between contexts (browse, clusterFiltered)
      await this.switchContext(contextKey)
      // Wait for Vue to update the DOM with the new photo list before scrolling
      await this.$nextTick()
      this.scrollToActivePhotoWithRetry()
      // Re-setup infinite scroll after context change
      setTimeout(() => this.setupInfiniteScroll(), 100)
    },
    formatDateTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString()
    },
    formatFileSize(bytes) {
      if (!bytes) return '-'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let size = bytes
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024
        i++
      }
      return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
    },
    async onDownload() {
      if (!this.currentPhoto?.id) return
      try {
        await this.downloadPhotos({
          ids: [this.currentPhoto.id],
          single_file: true
        })
      } catch (error) {
        this.notifyError(this.$pgettext('photoarchive.error', 'Download failed'))
      }
    },
    async onDownloadAll() {
      if (this.downloadingAll) return
      if (this.totalPhotos === 0) return

      this.downloadingAll = true
      try {
        const result = await this.downloadContextPhotos()
        if (result?.strategy === downloadStrategy.email) {
          this.notifySuccess(this.$pgettext('photoarchive.success', 'Download link will be sent to your email'))
        }
      } catch (error) {
        this.notifyError(this.$pgettext('photoarchive.error', 'Download failed'))
      } finally {
        this.downloadingAll = false
      }
    },
    async onUpdateMediaTags(tags) {
      await this.updateMediaTags(tags)
    },
    async onSaveFaceTags(tags) {
      await this.updateFaceTags({face_id: this.selectedFaceId, tags})
    },
    async onSaveClusterTags(tags) {
      await this.updateClusterTags({cluster_id: this.selectedCluster.id, tags})
    },
    async onMenuOpen({event, faceId}) {
      if (this.selectedFaceId !== faceId) {
        await this.selectFace(faceId)
        await this.$nextTick()
      }
      this.$refs.contextMenu.open(event)
    },
    async onContextMenuSelected(action) {
      if (!this.selectedFace) return

      if (action === 'view_cluster_photos') {
        this.onViewClusterPhotos()
        return
      }

      if (action === 'link_cluster_pica_code') {
        if (this.hasPerm('photo_search.archive.link_cluster_picacode') && this.selectedCluster) {
          const picaCode = await this.$bvModal.msgBoxPrompt(
            this.$pgettext('photoarchive.prompt', 'Inserisci il Pica Code da collegare:'),
            {
              title: this.$pgettext('photoarchive.title', 'Collega Pica Code'),
              okTitle: this.$pgettext('photoarchive.action', 'Collega'),
              cancelTitle: this.$pgettext('photoarchive.action', 'Annulla')
            }
          )
          if (picaCode) {
            await this.linkClusterPicaCode({
              cluster_id: this.selectedCluster.id,
              pica_code: picaCode
            })
            await this.fetchCurrentPhoto()
          }
        }
      } else if (action === 'unlink_cluster_pica_code') {
        if (this.hasPerm('photo_search.archive.unlink_cluster_picacode') && this.selectedCluster) {
          await this.confirm({
            color: 'danger',
            title: this.$pgettext('photoarchive.title', 'Scollega Pica Code'),
            icon: 'cipLinkBreak02',
            message: this.$pgettext('photoarchive.confirm', 'Scollegare il Pica Code da questo cluster?'),
            cb_confirm: async () => {
              await this.unlinkClusterPicaCode(this.selectedCluster.id)
              await this.fetchCurrentPhoto()
            }
          })
        }
      } else if (action === 'delete_cluster') {
        if (this.hasPerm('photo_search.archive.delete_cluster') && this.selectedCluster) {
          await this.confirm({
            color: 'danger',
            title: this.$pgettext('photoarchive.title', 'Elimina cluster'),
            icon: 'cipTrashFull',
            message: this.$pgettext('photoarchive.confirm', 'Eliminare questo cluster? Tutti i volti del cluster verranno rimossi.'),
            cb_confirm: async () => {
              const clusterId = this.selectedCluster.id
              if (this.selectedFaceId) {
                await this.selectFace(this.selectedFaceId)
              }
              await this.deleteCluster(clusterId)
              await this.fetchCurrentPhoto()
            }
          })
        }
      } else if (action === 'delete_face') {
        if (this.hasPerm('photo_search.archive.delete_face')) {
          await this.confirm({
            color: 'danger',
            title: this.$pgettext('photoarchive.title', 'Elimina volto'),
            icon: 'cipTrashFull',
            message: this.$pgettext('photoarchive.confirm', 'Eliminare questo volto?'),
            cb_confirm: async () => {
              const faceId = this.selectedFaceId
              if (this.selectedFaceId) {
                await this.selectFace(this.selectedFaceId)
              }
              await this.deleteFace(faceId)
              await this.fetchCurrentPhoto()
            }
          })
        }
      }
    }
  }
}
</script>


